import { FormArray } from '@angular/forms/src/model';
import { ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { Observable } from 'rxjs/Observable';
import { WarmUpService } from '../../../../services/warmup/warmup.service';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MdDialog, MdDialogRef } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { SelectItem } from 'primeng/primeng';
import { FormErrors, isValidMailFormat } from 'app/shared/validators/generic-validators';

import { User } from 'app/shared/models/user.model';
import { EntityTransactionProfileService } from 'app/entities/transaction-profile/entity-transaction-profile.service';
import { Risk } from 'app/entities/transaction-profile/risk.model';
import { RiskControl } from 'app/entities/transaction-profile/risk.model';

@Component({
    selector: 'update-risk',
    templateUrl: './update-risk.component.html',
    styleUrls: ['./update-risk.component.css']
})
/**
 * Component to add a new right.
 */
export class UpdateRiskComponent implements OnInit {
    agreementId: number;
    selectedRisk: Risk;
    updateRiskForm: FormGroup;
    loading = false;
    controlRiskChance = 'riskChance';
    controlRiskType = 'riskType';
    controlRiskImpact = 'riskImpact';
    riskControls = new Array<RiskControl>();
    riskControlsForm: FormArray;

    formErrors = {
        'name': '',
        'riskType': '',
    };

    validationMessages = {
        'name': {
            'required': 'Required field value.',
        },
        'riskType': {
            'required': 'Required field value.',
        }
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private fb: FormBuilder,
        private entityTransactionProfileService: EntityTransactionProfileService,
        private trackingService: TrackingService,
        private snackBarService: SnackBarService,
        private warmUpService: WarmUpService,
        private cdr: ChangeDetectorRef) { }

    ngOnInit() {
        this.buildForm();
        this.populateUpdateRiskForm();
        // Risk Chances from application values.
        this.setupControlValues(
            this.controlRiskChance, (this.warmUpService.getApplicationValue(ApplicationTypeConstants.ENTITY_RISK_CHANCE)));
        // Risk Types from application values.
        this.setupControlValues(this.controlRiskType, (this.warmUpService.getApplicationValue(ApplicationTypeConstants.ENTITY_RISK_TYPE)));
        // Risk Impacts from application values.
        this.setupControlValues(
            this.controlRiskImpact, (this.warmUpService.getApplicationValue(ApplicationTypeConstants.ENTITY_RISK_IMPACT)));
    }

    setupControlValues(control, dropDownValues: any) {
        if (dropDownValues instanceof Observable) {
            dropDownValues.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this[control] = response.values;
                    this.cdr.detectChanges();
                }
            );
        } else {
            this[control] = dropDownValues;
        }
    }

    buildForm() {
        this.updateRiskForm = this.fb.group({
            name: ['', Validators.required],
            scenario: '',
            grossChance: '',
            netChance: '',
            grossImpact: '',
            netImpact: '',
            type: '',
            riskControlsForm: this.fb.array([])
        });
        this.updateRiskForm.valueChanges.subscribe(data => this.onValueChanged(data));
        this.onValueChanged();
    }

    onValueChanged(data?: any) {
        if (!this.updateRiskForm) {
            return;
        }
        const form = this.updateRiskForm;
        for (const field in this.formErrors) {
            // clear previous error message (if any)
            this.formErrors[field] = '';
            const control = form.get(field);
            if (control && control.touched && !control.valid) {
                const messages = this.validationMessages[field];
                for (const key in control.errors) {
                    this.formErrors[field] += messages[key] + ' ';
                }
            }
        }
    }

    onSubmit({ value, valid }) {
        const risk = new Risk();
        risk.id = this.selectedRisk.id;
        risk.agreementId = this.agreementId;
        risk.description = value.scenario;
        risk.grossChance = value.grossChance;
        risk.grossImpact = value.grossImpact;
        risk.netChance = value.netChance;
        risk.netImpact = value.netImpact;
        risk.title = value.name;
        risk.riskControls = this.riskControls;
        risk.type = value.type;
        this.entityTransactionProfileService.updateRisk(risk).subscribe(
            response => {
                if (response.flagResult) {
                    this.dialogRef.close(response);
                }
                this.callSnackBarService(
                    response,
                    MessageService.RISK_SUCCESS_TEMPLATE.replace('%s', 'updated'),
                    MessageService.RISK_FAILURE_TEMPLATE.replace('%s', 'update'));
            },
            err => {
                this.callSnackBarService(
                    err,
                    MessageService.RISK_SUCCESS_TEMPLATE.replace('%s', 'updated'),
                    MessageService.RISK_FAILURE_TEMPLATE.replace('%s', 'update'));
            }
        );
    }

    callSnackBarService(response: any, successMsg: any, failureMsg: any) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }

    closeDialog(): void {
        this.updateRiskForm.reset();
        this.dialogRef.close();
    }

    populateUpdateRiskForm(): void {
        if (this.selectedRisk) {
            this.riskControls = this.selectedRisk.riskControls
                ? this.selectedRisk.riskControls : new Array<RiskControl>();
            this.updateRiskForm.patchValue({
                name: this.selectedRisk.title,
                scenario: this.selectedRisk.description,
                grossChance: this.selectedRisk.grossChance,
                grossImpact: this.selectedRisk.grossImpact,
                netChance: this.selectedRisk.netChance,
                netImpact: this.selectedRisk.netImpact,
                type: this.selectedRisk.type
            });
        }
    }

    hasRiskControl() {
        return this.riskControls.length > 0;
    }

    hasTempRiskControls() {
        const riskControlsForm = this.updateRiskForm.get('riskControlsForm') as FormArray;
        return riskControlsForm.length > 0;
    }

    moveToRiskControls(index: number) {
        const riskControlsForm = this.updateRiskForm.get('riskControlsForm') as FormArray;
        const tempRiskControl = new RiskControl();
        tempRiskControl.title = riskControlsForm.at(index).get('controlMeasure').value;
        tempRiskControl.chanceReduction = riskControlsForm.at(index).get('reductionOfChance').value;
        tempRiskControl.impactReduction = riskControlsForm.at(index).get('reductionOfImpact').value;

        this.entityTransactionProfileService.addRiskControl(this.selectedRisk.id, tempRiskControl).subscribe(response => {
            if (response.flagResult) {
                this.riskControls.push(tempRiskControl);
                this.riskControlsForm.removeAt(index);
                this.reloadRiskControls();
            }
            this.callSnackBarService(
                response,
                MessageService.RISK_CONTROL_SUCCESS_TEMPLATE.replace('%s', 'added'),
                MessageService.RISK_CONTROL_FAILURE_TEMPLATE.replace('%s', 'add'));
        }, err => {
            this.callSnackBarService(
                err,
                MessageService.RISK_CONTROL_SUCCESS_TEMPLATE.replace('%s', 'added'),
                MessageService.RISK_CONTROL_FAILURE_TEMPLATE.replace('%s', 'add'));
        });
    }

    reloadRiskControls() {
        this.entityTransactionProfileService.getRiskControls(this.selectedRisk.id).subscribe(response => {
            this.riskControls = response;
        });
    }

    deleteTempRiskControl(index: number) {
        this.riskControlsForm = this.updateRiskForm.get('riskControlsForm') as FormArray;
        this.riskControlsForm.removeAt(index);
    }

    createRiskControl(): FormGroup {
        return this.fb.group({
            controlMeasure: '',
            reductionOfChance: ['', Validators.compose([Validators.minLength(0), Validators.maxLength(3)])],
            reductionOfImpact: ['', Validators.compose([Validators.minLength(0), Validators.maxLength(3)])]
        });
    }

    addRiskControl(): void {
        this.riskControlsForm = this.updateRiskForm.get('riskControlsForm') as FormArray;
        this.riskControlsForm.push(this.createRiskControl());
    }

    deleteRiskControl(riskControl: RiskControl) {
        this.entityTransactionProfileService.deleteRiskControl(riskControl.id).subscribe(response => {
            if (response.flagResult) {
                this.reloadRiskControls();
            }
            this.callSnackBarService(
                response,
                MessageService.RISK_CONTROL_SUCCESS_TEMPLATE.replace('%s', 'deleted'),
                MessageService.RISK_CONTROL_FAILURE_TEMPLATE.replace('%s', 'delete'));
        }, err => {
            this.callSnackBarService(
                err,
                MessageService.RISK_CONTROL_SUCCESS_TEMPLATE.replace('%s', 'deleted'),
                MessageService.RISK_CONTROL_FAILURE_TEMPLATE.replace('%s', 'delete'));
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/risks/updaterisk/update-risk.component.ts